import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import ManageStock from "../../Issuer/Identification/Details/ManageStock";
import { Page, Input, Select, Button, Typography, Card } from "../../../../../design-system";
import { colors } from "../../../../../design-system/theme/colors";

import {
  getNFeConfig,
  updateNFeConfig,
  getNFeXMLAuth,
  createNFeXMLAuth,
  deleteNFeXMLAuth,
  correctNFeXMLAuth,
} from "../../../../../api/nfe";
import { formatDocument } from "../../../../../utils";
import InputMask from "react-input-mask";

const NFeConfig = () => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ show: false, title: "", description: "" });
  const [allowedDocs, setAllowedDocs] = useState([]);
  const [modalAddDocument, setModalAddDocument] = useState(false);
  const [documentType, setDocumentType] = useState("cpf");
  const [documentValue, setDocumentValue] = useState("");

  const fetchNFeConfig = async () => {
    setLoading(true);
    try {
      const fetchedConfig = await getNFeConfig();
      setConfig(fetchedConfig);
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }

    try {
      const docs = await getNFeXMLAuth();
      setAllowedDocs(docs);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchNFeConfig();
    window.scrollTo({ top: 0 });
  }, []);

  const updateLocalStorage = () => {
    let branch = window.localStorage.getItem("easygestorweb_current_branch");
    if (branch) {
      branch = JSON.parse(branch);
      branch["fiscal_config"] = config;
      window.localStorage.setItem(
        "easygestorweb_current_branch",
        JSON.stringify(branch)
      );
    }
  };

  const handleUpdateNFeConfig = async () => {
    setLoading(true);
    try {
      await updateNFeConfig(config);
      updateLocalStorage();
      if (window.location.search.includes("from=")) {
        window.location.href = window.location.search.split("from=")[1];
      }
      setModal({
        show: true,
        title: "Configurações atualizadas",
        description: "As configurações para emissão de NFe foram atualizadas!",
      });
    } catch (e) {
      setModal({
        show: true,
        title: "Oops...",
        description: "Ocorreu um erro ao tentar salvar as configurações, por favor revise as informações e tente novamente daqui a pouco",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (value === 'true') value = true;
    if (value === 'false') value = false;

    setConfig((prev) => ({ ...prev, [name]: value }));
  };

  const handleAuthorizeXMLAccess = async () => {
    const numericValue = documentValue.replace(/\D+/g, "");

    const isDuplicate = allowedDocs.some((doc) => doc.doc === numericValue);
    if (isDuplicate) {
      toast.warn("Este documento já está autorizado!", { position: "top-center" });
      return;
    }

    setLoading(true);

    const data = documentType === "cnpj" ? { cnpj: numericValue } : { cpf: numericValue };

    try {
      await createNFeXMLAuth(data);
      window.location.reload();
    } catch (e) {
      setLoading(false);
    }
  };

  const handleDeleteXMLAuth = async (id) => {
    if (loading) return;
    setLoading(true);
    try {
      await deleteNFeXMLAuth({ id });
      window.location.reload();
    } catch (e) {
      setLoading(false);
    }
  };

  const handleCorrectXMLAuth = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await correctNFeXMLAuth({});
      toast.success(`Foram removidos ${response.deleted} documentos duplicados!`, { position: "top-center" });
      fetchNFeConfig();
    } catch (e) {
      setLoading(false);
      toast.error("Ocorreu um erro ao corrigir os documentos.", { position: "top-center" });
    }
  };

  const booleanOptions = [
    { label: "Sim", value: 'true' },
    { label: "Não", value: 'false' },
  ];

  const documentOptions = [
    { label: "Pessoa Física (CPF)", value: "cpf" },
    { label: "Pessoa Jurídica (CNPJ)", value: "cnpj" },
  ];

  return (
    <Page
      title="Configurações para emissão de NF-e"
      subtitle="Ajuste as parametrizações fiscais padronizadas para suas notas."
    >
      <div className="row mb-3">
        {/* Seção 1: Dados de Emissão e Preferências */}
        <div className="col-12 mb-3">
          <Card>
            <Card.Header title="Dados de Emissão e Preferências" />
            <Card.Body>
              <div className="row">
                <div className="col-md-6 col-sm-12 mb-3">
                  <Input
                    data-cy-test="#nfe-settings-nfe-number"
                    name="nfe_number"
                    label="Número da NF-e"
                    onChange={handleChange}
                    value={config.nfe_number || ""}
                    disabled={loading}
                    placeholder="Ex: 1"
                  />
                </div>
                <div className="col-md-6 col-sm-12 mb-3">
                  <Input
                    data-cy-test="#nfe-settings-nfe-series"
                    name="nfe_serie"
                    label="Série"
                    onChange={handleChange}
                    value={config.nfe_serie || ""}
                    disabled={loading}
                    placeholder="Ex: 1"
                  />
                </div>
                <div className="col-md-6 col-sm-12 mb-3">
                  <Select
                    name="send_danfe_to_client"
                    label="Enviar a DANFE para o email do destinatário?"
                    options={booleanOptions}
                    onChange={handleChange}
                    value={config.send_danfe_to_client ? 'true' : 'false'}
                  />
                </div>
                <div className="col-md-6 col-sm-12 mb-3">
                  <Select
                    name="use_product_barcode"
                    label="Utilizar código de barras do produto?"
                    options={booleanOptions}
                    onChange={handleChange}
                    value={config.use_product_barcode ? 'true' : 'false'}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Seção 3: Autorização de XML */}
        <div className="col-12 mb-3">
          <Card>
            <Card.Header title="Autorização de XML" />
            <Card.Body>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Select
                    name="add_xml_auth"
                    label="Autorizar acesso ao XML da NFE para escritórios contábeis/terceiros?"
                    options={booleanOptions}
                    onChange={handleChange}
                    value={config.add_xml_auth ? 'true' : 'false'}
                  />
                </div>
              </div>

              {/* Lista de Autorizados (Aparece junto com a Seção 3 se habilitado) */}
              {config.add_xml_auth && (
                <div className="row mt-2 mx-0">
                  <div className="col-12 d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3 px-0" style={{ gap: '1rem' }}>
                    <Typography variant="cardTitle" style={{ color: colors.brandDarkBlue, margin: 0 }}>
                      Documentos Autorizados a Acessar
                    </Typography>
                    <div className="d-flex" style={{ gap: '10px' }}>
                      <Button variant="warning" onClick={handleCorrectXMLAuth} disabled={loading} style={{ color: '#000', backgroundColor: '#ffc107', border: 'none', fontWeight: 600 }}>
                        <i className="fas fa-broom mr-2"></i> Corrigir CNPJ de autorização
                      </Button>
                      <Button variant="primary" onClick={() => setModalAddDocument(true)}>
                        Adicionar Documento <i className="fas fa-plus ml-2"></i>
                      </Button>
                    </div>
                  </div>
                  
                  <div className="col-12 px-0">
                    <div className="border rounded" style={{ overflow: 'hidden' }}>
                      <div className="d-flex align-items-center" style={{ backgroundColor: '#f8f9fa', padding: '12px 20px', borderBottom: '1px solid #dee2e6' }}>
                        <Typography variant="paragraph" style={{ fontWeight: 600, color: colors.brandDarkBlue, margin: 0 }}>CNPJ/CPF</Typography>
                      </div>
                      {allowedDocs.length === 0 ? (
                          <div className="p-4 text-center">
                            <Typography variant="paragraph" style={{ color: colors.brandMediumGray, fontStyle: 'italic' }}>
                              Nenhum documento autorizado cadastrado. Clique no botão acima para adicionar.
                            </Typography>
                          </div>
                      ) : (
                        <ul className="list-group list-group-flush">
                          {allowedDocs.map((item, index) => {
                            const key = `nfe-config-authorized-document-${item.id}${index}`;
                            const document = formatDocument(item.doc);
                            return (
                              <li className="list-group-item d-flex justify-content-between align-items-center" key={key} style={{ padding: '12px 20px' }}>
                                <Typography variant="paragraph" style={{ margin: 0, color: '#495057' }}>{document}</Typography>
                                <div
                                  style={{ cursor: "pointer", backgroundColor: colors.error, color: 'white', borderRadius: '4px', width: '32px', height: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                  title="Remover Permissão"
                                  onClick={() => handleDeleteXMLAuth(item.id)}
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                    
                    <div className="mt-3 p-3 rounded d-flex align-items-center" style={{ backgroundColor: '#eef2fb', color: colors.brandDarkBlue, border: '1px solid #d8e2f3' }}>
                      <i className="fas fa-info-circle mr-3" style={{ fontSize: '1.2rem' }}></i>
                      <span style={{ fontSize: '0.95rem' }}>
                        <strong>Observação:</strong> Contribuintes da SEFAZ-BA são obrigados a manter o CNPJ 13.937.073/0001-56 listados.
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Seção 4: Informações Adicionais */}
      <div className="row mb-3">
        <div className="col-12 mb-3">
          <Card>
            <Card.Header title="Informações Adicionais & Estoque" />
            <Card.Body>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Input
                    name="nfe_fisco_information"
                    label="Informações do fisco"
                    onChange={handleChange}
                    value={config.nfe_fisco_information || ""}
                    disabled={loading}
                    placeholder="Insira notas complementares..."
                  />
                </div>
                <div className="col-md-6 mb-4 d-flex flex-column justify-content-end">
                  <ManageStock />
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>



      {/* Footer Actions */}
      <div className="row mt-4 pt-3 border-top">
        <div className="col-12 d-flex justify-content-end">
          <Button
            variant="primary"
            onClick={handleUpdateNFeConfig}
            disabled={loading}
            data-cy-test="#nfe-settings-button-save-settings"
          >
            {loading ? "Salvando..." : "Salvar Configurações"}
          </Button>
        </div>
      </div>

      {/* Modals */}
      <Modal show={modal.show} onHide={() => window.location.reload()}>
        <Modal.Header closeButton>
            <Typography variant="cardTitle" style={{ color: colors.brandDarkBlue }}>{modal.title}</Typography>
        </Modal.Header>
        <Modal.Body>
            <Typography variant="paragraph">{modal.description}</Typography>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={modalAddDocument}
        onHide={() => {
          setModalAddDocument(false);
          setDocumentType("cpf");
          setDocumentValue("");
        }}
      >
        <Modal.Header closeButton>
            <Typography variant="cardTitle" style={{ color: colors.brandDarkBlue }}>Autorizar Acesso ao XML</Typography>
        </Modal.Header>
        <Modal.Body>
            <Select
              label="Tipo de Documento"
              value={documentType}
              options={documentOptions}
              onChange={(e) => setDocumentType(e.target.value)}
              style={{ marginBottom: '1rem' }}
            />
            
            <Input
              type={documentType}
              label="Número do Documento"
              disabled={loading}
              autoFocus
              value={documentValue}
              onChange={(e) => setDocumentValue(e.target.value)}
              placeholder={`Informe o ${documentType.toUpperCase()}`}
            />
        </Modal.Body>
        <Modal.Footer>
            <Button variant="primary" onClick={handleAuthorizeXMLAccess} disabled={loading || !documentValue}>
              Salvar Autorização
            </Button>
        </Modal.Footer>
      </Modal>
    </Page>
  );
};

export default NFeConfig;



// WEBPACK FOOTER //
// ./src/views/Fiscal/NFe/Config/ConfigInForm/index.jsx